import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Card, Badge } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LogIn, Printer, Users, Pencil, Check, X, ExternalLink, BedDouble, FileStack } from "lucide-react";
import { AssignRoomDialog } from "./AssignRoomDialog";
import api, { formatApiError } from "@/lib/api";
import { docUrl, reservationRegCardUrl, fmtINR } from "@/lib/documents";
import { requestRegCard } from "@/components/documents/RegCardOptions";
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const groupRegCardUrl = (rid) => `${API_BASE}/documents/registration_card/reservation/${rid}/group/pdf`;
import { DownloadPdfButton } from "@/components/documents/DownloadPdfButton";
import { useCheckinGate, GateWrap } from "@/hooks/useCheckinGate";
import { EarlyCheckinPrompt } from "./EarlyCheckinPrompt";

const TONE = { pending: "warning", checked_in: "success", checked_out: "neutral", cancelled: "error" };
const LABEL = { pending: "Pending check-in", checked_in: "Checked in", checked_out: "Checked out", cancelled: "Cancelled" };

function GuestNameCell({ room, reservationId, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(room.guest_name_override ? room.guest_name : "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/pms/reservations/${reservationId}/rooms/${room.index}`, { guest_name: name }); toast.success(`Room ${room.room_number}: guest name saved`); setEditing(false); onSaved?.(); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  if (editing) {
    return (
      <div className="flex items-center gap-1" data-testid={`group-guest-edit-${room.index}`}>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Guest name for this room" className="h-8 text-[13px]" maxLength={120} data-testid={`group-guest-input-${room.index}`} />
        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={save} disabled={busy} data-testid={`group-guest-save-${room.index}`}><Check className="w-3.5 h-3.5" /></Button>
        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setEditing(false)} disabled={busy}><X className="w-3.5 h-3.5" /></Button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <span className="font-medium text-hocp-textPrimary truncate" data-testid={`group-guest-${room.index}`}>{room.guest_name}</span>
      {!room.guest_name_override && <span className="text-[10.5px] text-hocp-textMuted">(booker)</span>}
      {room.status === "pending" && (
        <button type="button" onClick={() => { setName(room.guest_name_override ? room.guest_name : ""); setEditing(true); }} className="text-hocp-textMuted hover:text-hocp-deepTeal" aria-label="Edit guest name" data-testid={`group-guest-edit-btn-${room.index}`}><Pencil className="w-3 h-3" /></button>
      )}
    </div>
  );
}

/** GROUP-CHECKIN · per-room status + "Check in this room" / "Check in all rooms" for multi-room reservations. */
export function GroupCheckInPanel({ reservationId, onChanged }) {
  const [group, setGroup] = useState(null);
  const [busy, setBusy] = useState(null); // "all" | room index
  const [prompt, setPrompt] = useState(null); // stays awaiting early check-in prompt
  const [assignLine, setAssignLine] = useState(null); // ROOM-TYPE-LINES · line awaiting a specific room
  const gate = useCheckinGate();

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/pms/reservations/${reservationId}/group`); setGroup(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, [reservationId]);
  useEffect(() => { load(); }, [load]);

  const handleError = (e, room) => {
    const body = e?.response?.data || {};
    const d = body.code ? body : body.detail || {};
    toast.error(d.message || d.reason || formatApiError(e), { description: room ? `Room ${room.room_number}` : undefined });
  };

  const toStay = (x) => ({ stay_id: x.stay_id, folio_id: x.folio_id, stay_code: x.stay_code, room_number: x.room_number, guest_name: x.guest_name });

  const checkInRoom = async (room) => {
    if (gate.isBlocked(group.arrival_date)) { toast.error(gate.reason(group.arrival_date)); return; }
    setBusy(room.index);
    try {
      const { data } = await api.post(`/pms/reservations/${reservationId}/check-in-room`, { room_index: room.index });
      toast.success(`Room ${data.room_number} checked in — ${data.stay_code}`, { description: `Registration card ${data.registration_card?.number}` });
      setPrompt([toStay(data)]);
    } catch (e) { handleError(e, room); }
    setBusy(null);
  };

  const checkInAll = async () => {
    if (gate.isBlocked(group.arrival_date)) { toast.error(gate.reason(group.arrival_date)); return; }
    setBusy("all");
    try {
      const { data } = await api.post(`/pms/reservations/${reservationId}/check-in-group`, {});
      const ok = data.checked_in || [];
      if (ok.length) toast.success(`${ok.length} room${ok.length === 1 ? "" : "s"} checked in`, { description: ok.map((x) => `Room ${x.room_number} · ${x.stay_code}`).join(" · ") });
      (data.failed || []).forEach((f) => toast.error(`Room ${f.room_index}: ${f.error}`));
      if (ok.length) setPrompt(ok.map(toStay)); else { load(); onChanged?.(); }
    } catch (e) { handleError(e); }
    setBusy(null);
  };

  const printAll = () => {
    if (!group) return;
    const live = group.rooms.filter((r) => r.stay_id);
    if (live.length === group.rooms.length && live.length === 1) requestRegCard({ url: docUrl("registration_card", live[0].stay_id), filename: "RegistrationCard.pdf" });
    else requestRegCard({ url: reservationRegCardUrl(reservationId), filename: `RegistrationCards-${group.code}.pdf`, title: "Individual Registration Cards" });
  };

  if (!group) return <Card className="p-4 text-hocp-textMuted text-[13px]">Loading group…</Card>;
  const pending = group.rooms.filter((r) => r.status === "pending");
  const gated = gate.isBlocked(group.arrival_date);
  const canCheckIn = pending.length > 0 && ["confirmed", "pending", "checked_in"].includes(group.status);
  const readyPending = pending.filter((r) => !r.needs_room);

  return (
    <Card className="p-4 space-y-4" data-testid="group-checkin-panel">
      <EarlyCheckinPrompt open={!!prompt} stays={prompt || []} onClose={() => { setPrompt(null); load(); onChanged?.(); }} />
      <AssignRoomDialog open={!!assignLine} onOpenChange={(v) => { if (!v) setAssignLine(null); }} excludeRoomIds={group.rooms.filter((x) => x.room_id && x.index !== assignLine?.index).map((x) => x.room_id)}
        reservation={assignLine ? { id: reservationId, code: `${group.code} · room ${assignLine.index}`, guest_name: assignLine.guest_name, hotel_id: group.hotel_id, status: group.status,
          arrival_date: group.arrival_date_full, departure_date: group.departure_date, requested_room_type_id: assignLine.room_type_id, room_type_name: assignLine.room_type, assigned_room_id: assignLine.room_id, room_number: assignLine.room_number } : null}
        onPick={async (roomId, room) => { await api.patch(`/pms/reservations/${reservationId}/rooms/${assignLine.index}`, { room_id: roomId }); toast.success(`Room ${room?.room_number || ""} assigned to line ${assignLine.index}`); load(); onChanged?.(); }} />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-[12px] mono uppercase tracking-[0.15em] text-slate-500 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />Group Check-In · {group.summary.total} rooms</h3>
          <div className="mt-1 flex items-center gap-2 text-[12.5px]" data-testid="group-summary">
            <Badge tone="success">{group.summary.checked_in} in-house</Badge>
            <Badge tone={pending.length ? "warning" : "neutral"}>{pending.length} pending</Badge>
            {group.summary.checked_out ? <Badge tone="neutral">{group.summary.checked_out} departed</Badge> : null}
            {group.partially_checked_in && <span className="text-hocp-textMuted">Partially checked in</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => requestRegCard({ url: groupRegCardUrl(reservationId), filename: `GroupRegistrationCard-${group.code}.pdf`, title: "Group Registration Card" })} data-testid="group-print-consolidated"><FileStack className="w-3.5 h-3.5 mr-1.5" />Print group card (1 page)</Button>
          <DownloadPdfButton url={groupRegCardUrl(reservationId)} filename={`GroupRegistrationCard-${group.code}.pdf`} label="Download group card" iconOnly regCard testId="group-download-consolidated" />
          <Button size="sm" variant="outline" onClick={printAll} data-testid="group-print-regcards"><Printer className="w-3.5 h-3.5 mr-1.5" />Print individual cards</Button>
          <DownloadPdfButton url={reservationRegCardUrl(reservationId)} filename={`RegistrationCards-${group.code}.pdf`} label="Download individual cards" iconOnly regCard testId="group-download-regcards" />
          {canCheckIn && (
            <GateWrap blocked={gated} reason={gate.reason(group.arrival_date)} testid="group-checkin-all-gate">
              <Button size="sm" onClick={checkInAll} disabled={!!busy || gated || !readyPending.length} title={!readyPending.length ? "Assign rooms first" : undefined} data-testid="group-checkin-all">
                <LogIn className="w-3.5 h-3.5 mr-1.5" />{busy === "all" ? "Checking in…" : `Check in all rooms (${readyPending.length}${group.unassigned_count ? ` · ${group.unassigned_count} need a room` : ""})`}
              </Button>
            </GateWrap>
          )}
        </div>
      </div>

      <div className="overflow-x-auto -mx-4 px-4">
        <table className="w-full text-[13px]" data-testid="group-rooms-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-hocp-textMuted border-b border-hocp-border">
              <th className="py-2 pr-3">#</th><th className="py-2 pr-3">Room</th><th className="py-2 pr-3">Guest</th><th className="py-2 pr-3 hidden md:table-cell">Pax</th>
              <th className="py-2 pr-3 hidden lg:table-cell">Rate</th><th className="py-2 pr-3">Status</th><th className="py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {group.rooms.map((r) => (
              <tr key={r.index} className="border-b border-hocp-borderSubtle last:border-0 align-middle" data-testid={`group-room-${r.index}`}>
                <td className="py-2.5 pr-3 mono text-hocp-textMuted">{r.index}</td>
                <td className="py-2.5 pr-3">
                  {r.needs_room && r.status === "pending"
                    ? <Button size="sm" className="h-8 bg-amber-500 hover:bg-amber-400 text-[#1A2425] font-semibold" onClick={() => setAssignLine(r)} data-testid={`group-assign-room-${r.index}`}><BedDouble className="w-3.5 h-3.5 mr-1" />Assign room</Button>
                    : <div className="font-semibold mono flex items-center gap-1.5">{r.room_number || "—"}{r.status === "pending" && <button type="button" onClick={() => setAssignLine(r)} className="text-[11px] font-normal text-hocp-textMuted hover:text-hocp-deepTeal" data-testid={`group-change-room-${r.index}`}><Pencil className="w-3 h-3 inline mr-0.5" />Change</button>}</div>}
                  <div className="text-[11px] text-hocp-textMuted">{r.room_type}</div>
                </td>
                <td className="py-2.5 pr-3 max-w-[220px]"><GuestNameCell room={r} reservationId={reservationId} onSaved={() => { load(); onChanged?.(); }} /></td>
                <td className="py-2.5 pr-3 hidden md:table-cell mono">{r.pax_adults}A {r.pax_children || 0}C</td>
                <td className="py-2.5 pr-3 hidden lg:table-cell mono">{r.rate_per_night ? fmtINR(r.rate_per_night) : "—"}</td>
                <td className="py-2.5 pr-3">
                  <Badge tone={TONE[r.status] || "neutral"} data-testid={`group-room-status-${r.index}`}>{LABEL[r.status] || r.status}</Badge>
                  {r.stay_code && <div className="mt-0.5 text-[11px] mono text-hocp-textMuted">{r.stay_code}</div>}
                </td>
                <td className="py-2.5">
                  <div className="flex flex-wrap justify-end gap-1.5">
                    <Button size="sm" variant="outline" className="h-8" title="Print registration card"
                      onClick={() => requestRegCard({ url: r.stay_id ? docUrl("registration_card", r.stay_id) : reservationRegCardUrl(reservationId, r.index), filename: `RegistrationCard-${r.registration_no || r.index}.pdf` })}
                      data-testid={`group-regcard-${r.index}`}><Printer className="w-3.5 h-3.5" /></Button>
                    {r.status === "pending" && canCheckIn && (
                      <GateWrap blocked={gated} reason={gate.reason(group.arrival_date)} testid={`group-checkin-gate-${r.index}`}>
                        <Button size="sm" className="h-8" onClick={() => checkInRoom(r)} disabled={!!busy || gated || r.needs_room} title={r.needs_room ? "Assign a room first" : undefined} data-testid={`group-checkin-${r.index}`}>
                          <LogIn className="w-3.5 h-3.5 mr-1" />{busy === r.index ? "…" : "Check in"}
                        </Button>
                      </GateWrap>
                    )}
                    {r.stay_id && (
                      <Button asChild size="sm" variant="outline" className="h-8"><Link to={`/pms/stays/${r.stay_id}`} data-testid={`group-open-stay-${r.index}`}><ExternalLink className="w-3.5 h-3.5 mr-1" />Stay</Link></Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
